import { useState, useRef, useEffect } from "react"
import CustomDate from "../utils/CustomDate"

const NavigationSlider = ({
	startDate = new CustomDate(1900, 0, 1), // Default start date: 1900
	endDate = new CustomDate(1950, 0, 1), // Default end date: 1950
	timeSkip = 10, // Default time skip: 10 years
	timeUnit = "year", // Time unit: 'year', 'month', 'day', etc.
	onDateChange, // Callback when date changes
	height = 30, // Default height
	visible_marker_info,
	current_date,
  min_window_width = 50
}) => {
	const MAX_BOX_COUNT = 100;
	const containerRef = useRef(null)
	const windowRef = useRef(null)
	const [isDragging, setIsDragging] = useState(false)
	const [windowPosition, setWindowPosition] = useState(0)
	const [containerDimensions, setContainerDimensions] = useState({
		width: 0,
		height: 0,
	})
	
	// Add this inside the useEffect where visible_marker_info is used (around line 76-84)
	// console.log('NavigationSlider received visible_marker_info:', visible_marker_info);
	// if (visible_marker_info) {
	// console.log('Has leftmostDate?', Boolean(visible_marker_info.leftmostDate));
	// console.log('Has rightmostDate?', Boolean(visible_marker_info.rightmostDate));
	// console.log('Raw visible_marker_info:', JSON.stringify(visible_marker_info));
	// }

	// Window dimensions
	const [windowDimensions, setWindowDimensions] = useState({
		width: min_window_width,
		height: height - 10, // Slightly smaller than container
	})

	const start_end_time_difference_abs = Math.abs(
		startDate.getTime() - endDate.getTime()
	)

	// Calculate position for a given date
	const calculatePositionForDate = (date) => {
		if (!date) return 0;
		
		const percentage = (date.getTime() - startDate.getTime()) / start_end_time_difference_abs;
		const position = percentage * containerDimensions.width;
		
		// Keep position within bounds
		return Math.max(
			0,
			Math.min(
				containerDimensions.width - windowDimensions.width,
				position - windowDimensions.width / 2
			)
		);
	}
	
	// Update window position when current_date changes (and not dragging)
	useEffect(() => {
		if (!isDragging && current_date && containerDimensions.width > 0 && windowDimensions.width) {
			const newPosition = calculatePositionForDate(current_date);
			setWindowPosition(newPosition);
		}
	}, [current_date, isDragging, containerDimensions.width, windowDimensions.width, startDate, endDate]);

	// Calculate the total number of intervals
	const calculateIntervals = () => {
		let tmp_interval_count
		if (timeUnit === "year")
			tmp_interval_count = Math.ceil((endDate.getFullYear() - startDate.getFullYear()) / timeSkip) // Math.ceil to include partial intervals (like 1950-1954)
		
		// Add more time unit calculations as needed
		
		return Math.min(Math.max(tmp_interval_count, 1),MAX_BOX_COUNT)
	}

	const totalIntervals = calculateIntervals()


	// Update container dimensions on mount and resize
	useEffect(() => {
		const updateDimensions = () => {
			if (containerRef.current) {
				const { width, height } =
					containerRef.current.getBoundingClientRect()
				setContainerDimensions({ width, height })
				if (visible_marker_info && visible_marker_info.leftmostDate) {
					const temp_window_width =
						(Math.abs(
							visible_marker_info.leftmostDate.getTime() -
								visible_marker_info.rightmostDate.getTime()
						) *
							width) /
						start_end_time_difference_abs
					setWindowDimensions((prev) => ({
						...prev,
						width: Math.max(temp_window_width,min_window_width) ,
					}))
				}
			}
		}

		updateDimensions()
		window.addEventListener("resize", updateDimensions)

		return () => window.removeEventListener("resize", updateDimensions)
	}, [containerRef, visible_marker_info])

	// FIXED: Correctly handle mouse events for dragging
	const handleMouseDown = (e) => {
		e.preventDefault()
		setIsDragging(true)
	}

	// Add separate mouseMove handler to the component
	useEffect(() => {
		const handleMouseMove = (e) => {
			if (!isDragging) return

			// Get container position
			const containerRect = containerRef.current.getBoundingClientRect()
			const relativeX = e.clientX - containerRect.left

			// Calculate new position keeping the window within bounds
			const newPosition = Math.max(
				0,
				Math.min(
					containerDimensions.width - windowDimensions.width,
					relativeX - windowDimensions.width / 2
				)
			)

			setWindowPosition(newPosition)

			// Calculate date at the middle point of the window
			const middlePoint = newPosition + windowDimensions.width / 2
			const dateAtMiddle = calculateDateAtPosition(middlePoint)

			// Call the onDateChange function
			if (onDateChange && dateAtMiddle) {
				onDateChange(dateAtMiddle)
			}
		}

		const handleMouseUp = () => {
			setIsDragging(false)
		}

		if (isDragging) {
			document.addEventListener("mousemove", handleMouseMove)
			document.addEventListener("mouseup", handleMouseUp)
		}


		return () => {
			document.removeEventListener("mousemove", handleMouseMove)
			document.removeEventListener("mouseup", handleMouseUp)
		}
	}, [isDragging, containerDimensions, windowDimensions])

	// Calculate the date at a specific position
	const calculateDateAtPosition = (position) => {
		// Calculate percentage of position relative to total width
		const percentage = position / containerDimensions.width

		// Calculate the position within the total time range
		if (timeUnit === "year") {
			const timediff = endDate.getTime() - startDate.getTime()
			const new_time = startDate.getTime() + Math.floor(timediff * percentage)
			
			// Create a new CustomDate object instead of a standard Date
			const newDate = new Date(new_time)
			return new CustomDate(
				newDate.getFullYear(),
				newDate.getMonth(),
				newDate.getDate(),
				newDate.getFullYear() < 0 // isBC if year is negative
			)
		}

		return null
	}

	// Generate interval boxes based on time range and skip - FIXED: correct calculation for interval boxes
	const renderIntervalBoxes = () => {
		const boxes = []
		const boxWidth = containerDimensions.width / totalIntervals

		for (let i = 0; i < totalIntervals; i++) {
			const yearValue = startDate.getFullYear() + i * timeSkip
			// const endYearValue = yearValue + timeSkip - 1 // End year dahil. Eğer yazıda end year gözükmesini istiyorsak bunu da açarız

			boxes.push(
				<div
					key={i}
					style={{
						position: "absolute",
						left: `${i * boxWidth}px`,
						top: "0",
						width: `${Math.min(boxWidth, 50)}px`, // Maksimum genişliği 50px ile sınırla
						height: "100%",
						borderRight:
							i < totalIntervals - 1 ? "1px solid #ccc" : "none",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: "11px", // Yazı boyutunu küçült
						color: "#000000",
						overflow: "hidden", // Taşan metni gizle
						textOverflow: "ellipsis", // Taşan metni ... ile göster
						whiteSpace: "nowrap", // Metni tek satırda tut
					}}
				>
					{yearValue}
				</div>
			)
		}

		return boxes
	}

	return (
		<div
			ref={containerRef}
			style={{
				position: "relative",
				width: "100%",
				height: `${height}px`,
				backgroundColor: "#A1E3F9",
				borderRadius: "4px",
				overflow: "hidden",
				userSelect: "none",
			}}
		>
			{/* Background with interval boxes */}
			<div
				style={{
					position: "relative",
					width: "100%",
					height: "100%",
					overflow: "hidden",
				}}
			>
				{containerDimensions.width > 0 && renderIntervalBoxes()}
			</div>

			{/* Draggable window */}
			<div
				ref={windowRef}
				style={{
					position: "absolute",
					left: `${windowPosition}px`,
					top: "5px",
					width: `${windowDimensions.width}px`,
					height: `${windowDimensions.height}px`,
					backgroundColor: "transparent", // Set background to transparent
					borderLeft: "2px solid red", // Left border in red
					borderRight: "2px solid red", // Right border in red
					borderTop: "none", // Remove top border
					borderBottom: "none", // Remove bottom border
					cursor: isDragging ? "grabbing" : "grab",
					zIndex: 10,
				}}
				onMouseDown={handleMouseDown}
				// Add touch events for mobile support
				onTouchStart={(e) => {
					e.preventDefault()
					setIsDragging(true)
				}}
			/>
		</div>
	)
}

export default NavigationSlider
